// src/components/Rules/RulesPage.js — Auto-categorization rules management page (F6)
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Typography, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, Paper, Divider
} from '@mui/material';
import AddIcon         from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RuleCard  from './RuleCard';
import RuleForm  from './RuleForm';
import { useBudget } from '../../contexts/BudgetContext';
import api from '../../services/api';

const RulesPage = () => {
  const { categories } = useBudget();

  const [rules,   setRules]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  // Dialog state
  const [formOpen,     setFormOpen]     = useState(false);
  const [editingRule,  setEditingRule]  = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [testTarget,   setTestTarget]   = useState(null);
  const [deleting,     setDeleting]     = useState(false);

  // Snackbar
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const showSnack = (message, severity = 'success') => setSnack({ open: true, message, severity });

  // ── Load rules ──────────────────────────────────────────────────────────────
  const loadRules = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/rules');
      setRules(data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRules(); }, [loadRules]);

  // ── Category lookup helper ──────────────────────────────────────────────────
  const getCategoryName = (categoryId) => {
    if (!categoryId) return 'Unknown';
    const cat = categories.find(c => {
      const id = c.id || c._id;
      return String(id) === String(categoryId);
    });
    return cat ? `${cat.icon ? cat.icon + ' ' : ''}${cat.name}` : String(categoryId).slice(-6);
  };

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleEdit   = (rule) => { setEditingRule(rule); setFormOpen(true); };
  const handleCreate = ()     => { setEditingRule(null);  setFormOpen(true); };
  const handleFormClose = ()  => { setFormOpen(false); setEditingRule(null); };

  const handleSaved = () => {
    showSnack(editingRule ? 'Rule updated' : 'Rule created');
    loadRules();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/rules/${deleteTarget._id}`);
      showSnack('Rule deleted');
      setDeleteTarget(null);
      loadRules();
    } catch (err) {
      showSnack(err.response?.data?.message || 'Failed to delete rule', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (rule) => {
    try {
      await api.put(`/rules/${rule._id}`, { isActive: !rule.isActive });
      showSnack(rule.isActive ? 'Rule disabled' : 'Rule enabled');
      loadRules();
    } catch (err) {
      showSnack('Failed to toggle rule', 'error');
    }
  };

  // ── Reorder helpers ─────────────────────────────────────────────────────────
  const reorder = async (fromIdx, toIdx) => {
    const reordered = [...rules];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);

    // Recalculate priorities: highest index in array = highest priority
    const total = reordered.length;
    const updates = reordered.map((r, i) => ({
      id:       r._id,
      priority: total - i          // first card = highest priority
    }));

    // Optimistic update
    setRules(reordered.map((r, i) => ({ ...r, priority: total - i })));

    try {
      await api.patch('/rules/reorder', updates);
    } catch (err) {
      showSnack('Failed to save order', 'error');
      loadRules(); // roll back
    }
  };

  const handleMoveUp   = (rule) => {
    const idx = rules.findIndex(r => r._id === rule._id);
    if (idx > 0) reorder(idx, idx - 1);
  };
  const handleMoveDown = (rule) => {
    const idx = rules.findIndex(r => r._id === rule._id);
    if (idx < rules.length - 1) reorder(idx, idx + 1);
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box maxWidth={860} mx="auto" px={2} py={3}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <AutoFixHighIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>
            Auto-categorization Rules
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          disabled={loading}
        >
          Add Rule
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" mb={3}>
        Rules automatically assign categories to new transactions based on description, merchant name,
        or amount — so you spend less time categorizing manually. Rules are applied in priority order
        (top first); the first matching rule wins.
      </Typography>

      <Divider sx={{ mb: 3 }} />

      {/* Loading */}
      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {/* Error */}
      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* Empty state */}
      {!loading && !error && rules.length === 0 && (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center' }}>
          <AutoFixHighIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No rules yet
          </Typography>
          <Typography variant="body2" color="text.disabled" mb={3}>
            Create your first rule to automatically categorize transactions.
            <br />
            For example: when description <em>contains</em> "Netflix" → set category to Entertainment.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
            Create first rule
          </Button>
        </Paper>
      )}

      {/* Rule list */}
      {!loading && rules.map((rule, idx) => (
        <RuleCard
          key={rule._id}
          rule={rule}
          categoryName={getCategoryName(rule.actions?.categoryId)}
          onEdit={handleEdit}
          onDelete={setDeleteTarget}
          onTest={setTestTarget}
          onToggleActive={handleToggleActive}
          onMoveUp={handleMoveUp}
          onMoveDown={handleMoveDown}
          isFirst={idx === 0}
          isLast={idx === rules.length - 1}
        />
      ))}

      {/* Create / Edit form */}
      <RuleForm
        open={formOpen}
        onClose={handleFormClose}
        onSaved={handleSaved}
        editingRule={editingRule}
      />

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Rule</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>"{deleteTarget?.name}"</strong>?
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test result dialog (quick view) */}
      <Dialog open={!!testTarget} onClose={() => setTestTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>Test Rule: {testTarget?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            To test this rule, open it in edit mode and use the Test Panel at the bottom.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setTestTarget(null); handleEdit(testTarget); }} variant="contained">
            Open in editor
          </Button>
          <Button onClick={() => setTestTarget(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RulesPage;
